import { useEffect } from 'react';
import { NavLink, Navigate, Outlet, useLocation } from 'react-router-dom';
import { CenteredSpinner } from '../components/Spinner.tsx';
import { NavIcon, type IconName } from '../components/NavIcon.tsx';
import { useAdminAuth } from '../lib/auth/admin.tsx';
import { useBranding } from '../lib/branding.tsx';
import { companyInfoLabel } from '../lib/companyInfo.ts';
import { teamRegionNames, useTeamRegions } from '../lib/teamRegions.ts';

// Accent palette adopted from the admin portal design prototype. These
// intentionally override the tenant brand-primary for the shell chrome.
const ACCENT = '#2B93FF';
const ACCENT_LIGHT = '#E8F4FF';

type NavGroup = 'overview' | 'operate' | 'configure' | 'monitor' | 'admin';
type NavRow = {
  to: string;
  label: string;
  icon: IconName;
  group: NavGroup;
  end?: boolean;
};

const NAV: NavRow[] = [
  { to: '/admin', label: 'Dashboard', icon: 'dashboard', group: 'overview', end: true },
  { to: '/admin/companies', label: 'Companies', icon: 'building', group: 'operate' },
  { to: '/admin/drivers', label: 'Drivers', icon: 'users', group: 'operate' },
  { to: '/admin/assets', label: 'Assets', icon: 'truck', group: 'operate' },
  { to: '/admin/verifications', label: 'Document verification', icon: 'shield-check', group: 'operate' },
  { to: '/admin/corrective-actions', label: 'Corrective actions', icon: 'clipboard-check', group: 'operate' },
  { to: '/admin/visitors', label: 'Visitors', icon: 'user-check', group: 'operate' },
  { to: '/admin/expiring-documents', label: 'Expiring documents', icon: 'inbox', group: 'operate' },
  { to: '/admin/questionnaires', label: 'Questionnaires', icon: 'list-checks', group: 'configure' },
  { to: '/admin/documents', label: 'Documents', icon: 'file-text', group: 'configure' },
  { to: '/admin/document-types', label: 'Document types', icon: 'clipboard-check', group: 'configure' },
  { to: '/admin/training', label: 'Training', icon: 'graduation-cap', group: 'configure' },
  { to: '/admin/library', label: 'Document library', icon: 'book', group: 'configure' },
  { to: '/admin/reports', label: 'Reports', icon: 'bar-chart', group: 'monitor' },
  { to: '/admin/settings', label: 'Settings', icon: 'settings', group: 'admin' },
];

const GROUP_LABEL: Record<NavGroup, string> = {
  overview: 'Overview',
  operate: 'Operate',
  configure: 'Configure',
  monitor: 'Monitor',
  admin: 'Admin',
};

type GroupedRow =
  | { kind: 'header'; key: string; label: string }
  | { kind: 'item'; key: string; row: NavRow };

function groupNav(rows: NavRow[]): GroupedRow[] {
  const out: GroupedRow[] = [];
  let last: NavGroup | null = null;
  for (const r of rows) {
    if (r.group !== last) {
      out.push({ kind: 'header', key: `h-${r.group}`, label: GROUP_LABEL[r.group] });
      last = r.group;
    }
    out.push({ kind: 'item', key: r.to, row: r });
  }
  return out;
}

function initials(name: string | undefined): string {
  if (!name) return 'U';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'U';
  const first = parts[0] ?? '';
  if (parts.length === 1) return first.slice(0, 2).toUpperCase() || 'U';
  const last = parts[parts.length - 1] ?? '';
  return `${first[0] ?? ''}${last[0] ?? ''}`.toUpperCase() || 'U';
}

export function AdminShell() {
  const { user, loading, signOut, activeTeamRegion, setActiveTeamRegion } = useAdminAuth();
  const { branding } = useBranding();
  const location = useLocation();
  // Auditors have a scoped portal and no team-region access — don't fetch regions
  // for them (the endpoint is forbidden to auditors) and redirect them below.
  const regionsQuery = useTeamRegions(!!user && user.role !== 'auditor');

  const assignedNames = (user?.team_regions ?? []).map((r) => r.name);
  const isRestricted = assignedNames.length > 0;
  const regionOptions = isRestricted ? assignedNames : teamRegionNames(regionsQuery.data);

  // Reset a stale selection (a region that was renamed/deleted, or fell outside
  // the user's assignment) once the live option list is known.
  useEffect(() => {
    if (!user || activeTeamRegion === null || regionsQuery.data === undefined) return;
    if (!regionOptions.includes(activeTeamRegion)) setActiveTeamRegion(null);
  }, [user, activeTeamRegion, regionsQuery.data]);

  if (loading) return <CenteredSpinner />;
  if (!user) {
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }
  // Auditors don't get the admin shell — send them to their own portal.
  if (user.role === 'auditor') {
    return <Navigate to="/audit" replace />;
  }

  const regionLabel = companyInfoLabel(branding, 'teamRegion');
  const allLabel = isRestricted
    ? `All my ${regionLabel.toLowerCase()}s`
    : `All ${regionLabel.toLowerCase()}s`;
  const brandName = branding.companyName?.replace(/ Safety.*$/i, '') ?? 'Locate2u';
  const rows = groupNav(NAV);

  return (
    <div className="flex min-h-screen" style={{ background: '#F8FAFC' }}>
      {/* ===== Sidebar ===== */}
      <aside
        className="relative flex w-[260px] shrink-0 flex-col bg-white"
        style={{ borderRight: '1px solid #E2E8F0' }}
      >
        {/* Locate2u brand logo + portal sublabel (tenant logo lives in the top bar) */}
        <div className="px-5 pb-4 pt-5" style={{ borderBottom: '1px solid #F1F5F9' }}>
          <a href="https://www.locate2u.com" target="_blank" rel="noopener noreferrer">
            <img src="/locate2u-logo.png" alt="Locate2u" className="h-8 w-auto" />
          </a>
          <div className="mt-2 text-[13px]" style={{ color: '#64748B' }}>
            Admin Portal
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto px-3 py-4">
          {rows.map((row) => {
            if (row.kind === 'header') {
              return (
                <div
                  key={row.key}
                  className="font-mono-eyebrow px-3 pb-1.5 pt-4 text-[9.5px] tracking-[0.14em]"
                  style={{ color: '#94A3B8' }}
                >
                  {row.label.toUpperCase()}
                </div>
              );
            }
            const item = row.row;
            return (
              <NavLink
                key={row.key}
                to={item.to}
                end={item.end ?? false}
                className={({ isActive }) =>
                  `relative flex items-center gap-3 rounded-lg px-3.5 py-2.5 text-[15px] transition ${
                    isActive ? 'font-semibold' : 'font-medium hover:bg-slate-50'
                  }`
                }
                style={({ isActive }) =>
                  isActive ? { background: ACCENT_LIGHT, color: ACCENT } : { color: '#64748B' }
                }
              >
                {({ isActive }) => (
                  <>
                    <NavIcon
                      name={item.icon}
                      size={20}
                      style={{ color: isActive ? ACCENT : '#94A3B8' }}
                    />
                    <span className="flex-1 text-left">{item.label}</span>
                  </>
                )}
              </NavLink>
            );
          })}
        </nav>

        {/* Active team region switcher (scopes the operate lists) */}
        <div className="px-5 pb-1 pt-3" style={{ borderTop: '1px solid #F1F5F9' }}>
          <div
            className="font-mono-eyebrow pb-1.5 text-[9.5px] tracking-[0.14em]"
            style={{ color: '#94A3B8' }}
          >
            ACTIVE {regionLabel.toUpperCase()}
          </div>
          <select
            value={activeTeamRegion ?? ''}
            onChange={(e) => setActiveTeamRegion(e.target.value === '' ? null : e.target.value)}
            className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-slate-400 focus:outline-none"
            aria-label={`Active ${regionLabel}`}
          >
            <option value="">{allLabel}</option>
            {regionOptions.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </div>

        {/* Footer: real user + sign out */}
        <div className="px-3 pb-4 pt-3" style={{ borderTop: '1px solid #F1F5F9' }}>
          <button
            type="button"
            onClick={() => void signOut()}
            className="flex w-full items-center gap-3 rounded-lg p-2 text-left transition hover:bg-slate-50"
            title={`Sign out ${user.email}`}
          >
            <div
              className="font-mono-eyebrow flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[12px] font-bold"
              style={{ background: ACCENT_LIGHT, color: ACCENT }}
            >
              {initials(user.name)}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-[13px] font-semibold" style={{ color: '#1E293B' }}>
                {user.name}
              </div>
              <div className="truncate text-[11px]" style={{ color: '#64748B' }}>
                {user.role === 'admin' ? 'Admin' : 'Reviewer'} ·{' '}
                {isRestricted ? assignedNames.join(', ') : `All ${regionLabel}s`}
              </div>
            </div>
            <NavIcon name="log-out" size={16} style={{ color: '#94A3B8' }} />
          </button>
        </div>
      </aside>

      {/* ===== Main: top bar + content ===== */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Top header bar */}
        <header
          className="flex items-center gap-4 bg-white px-8 py-4"
          style={{ borderBottom: '1px solid #E2E8F0' }}
        >
          {branding.logoUrl ? (
            <img src={branding.logoUrl} alt={brandName} className="h-7 w-auto" />
          ) : (
            <span className="text-[16px] font-bold tracking-tight" style={{ color: '#1E293B' }}>
              {brandName}
            </span>
          )}
          <div className="h-6 w-px" style={{ background: '#E2E8F0' }} />
          <div className="text-[15px]" style={{ color: '#64748B' }}>
            Powered by{' '}
            <a
              href="https://www.locate2u.com"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold hover:underline"
              style={{ color: ACCENT }}
            >
              Locate2u
            </a>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto" style={{ background: '#F8FAFC' }}>
          <div className="px-8 py-6">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
